import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as authService from "../services/auth";

const STORAGE_ACCESS_TOKEN_KEY = "casaclass:access";
const STORAGE_REFRESH_TOKEN_KEY = "casaclass:refresh";
const STORAGE_USER_KEY = "casaclass:user";
const STORAGE_ACTIVE_STUDENT_KEY = "casaclass:active-student";
const TOKEN_RENEW_MARGIN_MS = 60 * 1000;

// Reads the `exp` claim (seconds) of a JWT; returns milliseconds or null.
function getTokenExpiry(token) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === "number" ? exp * 1000 : null;
  } catch (error) {
    return null;
  }
}

const AuthContext = createContext({
  user: null,
  accessToken: null,
  loading: true,
  login: async () => {},
  loginWithGoogle: async () => {},
  register: async () => {},
  logout: async () => {},
  refreshProfile: async () => {},
  updateUser: () => {},
  activeStudentId: null,
  setActiveStudent: () => {},
  effectiveUser: null,
  effectiveRole: null,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem(STORAGE_USER_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [accessToken, setAccessToken] = useState(() =>
    localStorage.getItem(STORAGE_ACCESS_TOKEN_KEY)
  );
  const [refreshToken, setRefreshToken] = useState(() =>
    localStorage.getItem(STORAGE_REFRESH_TOKEN_KEY)
  );
  // With a saved session the app shows at once and the session is checked in
  // the background; only a first visit waits for the check.
  const [loading, setLoading] = useState(
    () =>
      !(
        localStorage.getItem(STORAGE_USER_KEY) &&
        localStorage.getItem(STORAGE_ACCESS_TOKEN_KEY)
      ),
  );
  const [activeStudentId, setActiveStudentId] = useState(() => {
    const stored = localStorage.getItem(STORAGE_ACTIVE_STUDENT_KEY);
    return stored ? Number(stored) : null;
  });

  const persistAuth = useCallback(
    (nextAccessToken, nextRefreshToken, nextUser) => {
      if (typeof nextAccessToken !== "undefined") {
        setAccessToken(nextAccessToken);
        if (nextAccessToken === null) {
          localStorage.removeItem(STORAGE_ACCESS_TOKEN_KEY);
        } else {
          localStorage.setItem(STORAGE_ACCESS_TOKEN_KEY, nextAccessToken);
        }
      }
      if (typeof nextRefreshToken !== "undefined") {
        setRefreshToken(nextRefreshToken);
        if (nextRefreshToken === null) {
          localStorage.removeItem(STORAGE_REFRESH_TOKEN_KEY);
        } else {
          localStorage.setItem(STORAGE_REFRESH_TOKEN_KEY, nextRefreshToken);
        }
      }
      if (typeof nextUser !== "undefined") {
        setUser(nextUser);
        if (nextUser === null) {
          localStorage.removeItem(STORAGE_USER_KEY);
          localStorage.removeItem(STORAGE_ACTIVE_STUDENT_KEY);
          setActiveStudentId(null);
        } else {
          localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(nextUser));
          if (nextUser.role !== "parent") {
            localStorage.removeItem(STORAGE_ACTIVE_STUDENT_KEY);
            setActiveStudentId(null);
          }
        }
      }
    },
    []
  );

  const clearAuth = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setActiveStudentId(null);
    localStorage.removeItem(STORAGE_ACCESS_TOKEN_KEY);
    localStorage.removeItem(STORAGE_REFRESH_TOKEN_KEY);
    localStorage.removeItem(STORAGE_USER_KEY);
    localStorage.removeItem(STORAGE_ACTIVE_STUDENT_KEY);
  }, []);

  useEffect(() => {
    let ignore = false;

    async function hydrate() {
      if (!accessToken) {
        setLoading(false);
        return;
      }

      try {
        const profile = await authService.me(accessToken);
        if (!ignore) {
          persistAuth(accessToken, refreshToken, profile.user);
          setLoading(false);
        }
      } catch (error) {
        if (ignore) {
          return;
        }

        // Server unreachable (no HTTP status): keep the saved session instead
        // of logging the user out; the next request will try again.
        if (!error?.status) {
          setLoading(false);
          return;
        }

        if (!refreshToken) {
          clearAuth();
          setLoading(false);
          return;
        }

        try {
          const refreshed = await authService.refresh(refreshToken);
          if (ignore) {
            return;
          }
          const nextAccess = refreshed.access;
          const nextRefresh = refreshed.refresh || refreshToken;
          const profile = await authService.me(nextAccess);
          if (!ignore) {
            persistAuth(nextAccess, nextRefresh, profile.user);
            setLoading(false);
          }
        } catch (refreshError) {
          if (!ignore) {
            if (refreshError?.status) clearAuth();
            setLoading(false);
          }
        }
      }
    }

    hydrate();
    return () => {
      ignore = true;
    };
  }, [accessToken, refreshToken, clearAuth, persistAuth]);

  // Renew the access token shortly before it expires. Without this, every API
  // call fails once the token's lifetime (15 min by default) has passed, until
  // the page is reloaded.
  useEffect(() => {
    if (!accessToken || !refreshToken) return undefined;

    const expiresAt = getTokenExpiry(accessToken);
    if (!expiresAt) return undefined;

    let cancelled = false;
    let inFlight = false;

    const renew = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const refreshed = await authService.refresh(refreshToken);
        if (!cancelled && refreshed?.access) {
          persistAuth(refreshed.access, refreshed.refresh || refreshToken);
        }
      } catch (error) {
        // A rejected refresh token means the session is over; network errors
        // are left alone so a flaky connection doesn't log the user out.
        if (!cancelled && (error?.status === 401 || error?.status === 403)) {
          clearAuth();
        }
      } finally {
        inFlight = false;
      }
    };

    const delay = Math.max(expiresAt - Date.now() - TOKEN_RENEW_MARGIN_MS, 0);
    const timer = setTimeout(renew, delay);

    // Timers are paused while a laptop sleeps, so check again on return.
    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        expiresAt - Date.now() < TOKEN_RENEW_MARGIN_MS
      ) {
        renew();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [accessToken, refreshToken, clearAuth, persistAuth]);

  const login = useCallback(
    async (email, password) => {
      const result = await authService.login(email, password);
      persistAuth(result.access, result.refresh, result.user);
      return result.user;
    },
    [persistAuth]
  );

  const loginWithGoogle = useCallback(
    async (idToken) => {
      const result = await authService.loginWithGoogle(idToken);
      persistAuth(result.access, result.refresh, result.user);
      return result.user;
    },
    [persistAuth]
  );

  const register = useCallback(
    async ({ name, email, password, role, resume, phoneNumber }) => {
      const result = await authService.register({
        name,
        email,
        password,
        role,
        resume,
        phoneNumber,
      });
      persistAuth(result.access, result.refresh, result.user);
      return result.user;
    },
    [persistAuth]
  );

  const logout = useCallback(async () => {
    if (accessToken || refreshToken) {
      try {
        await authService.logout({ accessToken, refreshToken });
      } catch (error) {
        // Ignore network errors on logout, clear client state regardless.
      }
    }
    clearAuth();
  }, [accessToken, refreshToken, clearAuth]);

  const refreshProfile = useCallback(async () => {
    if (!accessToken) {
      return null;
    }
    const profile = await authService.me(accessToken);
    persistAuth(undefined, undefined, profile.user);
    return profile.user;
  }, [accessToken, persistAuth]);

  const updateUser = useCallback(
    (nextUser) => {
      persistAuth(undefined, undefined, nextUser);
    },
    [persistAuth]
  );

  const setActiveStudent = useCallback(
    (studentId) => {
      if (!user || user.role !== "parent") {
        setActiveStudentId(null);
        localStorage.removeItem(STORAGE_ACTIVE_STUDENT_KEY);
        return;
      }

      if (studentId === null || typeof studentId === "undefined") {
        setActiveStudentId(null);
        localStorage.removeItem(STORAGE_ACTIVE_STUDENT_KEY);
        return;
      }

      const normalized = Number(studentId);
      setActiveStudentId(normalized);
      localStorage.setItem(STORAGE_ACTIVE_STUDENT_KEY, String(normalized));
    },
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      accessToken,
      loading,
      login,
      loginWithGoogle,
      register,
      logout,
      refreshProfile,
      updateUser,
      activeStudentId,
      setActiveStudent,
      effectiveUser: (() => {
        if (user && user.role === "parent" && activeStudentId) {
          return { ...user, role: "student", student_id: activeStudentId };
        }
        return user;
      })(),
      effectiveRole: (() => {
        if (user && user.role === "parent" && activeStudentId) {
          return "student";
        }
        return user?.role || null;
      })(),
    }),
    [
      user,
      accessToken,
      loading,
      login,
      loginWithGoogle,
      register,
      logout,
      refreshProfile,
      updateUser,
      activeStudentId,
      setActiveStudent,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
